import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDownToLine,
  Boxes,
  FileArchive,
  GitCompareArrows,
  PackagePlus,
  Pencil,
  Plus,
  Trash2,
  UploadCloud,
} from 'lucide-react';
import { useParams } from 'react-router-dom';
import {
  checkApkExists,
  checkApkReferences,
  createChannel,
  deleteApk,
  deleteChannel,
  deletePatch,
  downloadApk,
  generatePatch,
  getRunningPatchTasks,
  listApks,
  listChannels,
  listPatches,
  renameChannel,
  updateChannelVersion,
  uploadApk,
} from '../api/AppOtaApi';
import ApkSelect, { formatApkVersion } from '../components/ApkSelect';
import { ActionDrawer, ConfirmDialog, Toast } from '../components/ui';
import styles from './AppUpdateChannelMgr.module.scss';

const views = [
  { id: 'channels', label: 'Rollout channels', icon: Boxes },
  { id: 'artifacts', label: 'APK library', icon: FileArchive },
  { id: 'patches', label: 'Delta patches', icon: GitCompareArrows },
];

function AppUpdateChannelMgr() {
  const { packageName } = useParams();
  const [activeView, setActiveView] = useState('channels');
  const [channels, setChannels] = useState([]);
  const [apks, setApks] = useState([]);
  const [patches, setPatches] = useState([]);
  const [runningPatchTasks, setRunningPatchTasks] = useState([]);
  const [apkReferences, setApkReferences] = useState({});
  const [pendingVersions, setPendingVersions] = useState({});
  const [drawer, setDrawer] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [newChannelName, setNewChannelName] = useState('');
  const [newChannelVersion, setNewChannelVersion] = useState('');
  const [renameOldName, setRenameOldName] = useState('');
  const [renameNewName, setRenameNewName] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [patchFromVersion, setPatchFromVersion] = useState('');
  const [patchToVersion, setPatchToVersion] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const fetchChannels = useCallback(async () => {
    const response = await listChannels(packageName);
    setChannels(response.data || []);
  }, [packageName]);

  const fetchApks = useCallback(async () => {
    const response = await listApks(packageName);
    setApks(response.data || []);
  }, [packageName]);

  const fetchPatches = useCallback(async () => {
    const response = await listPatches(packageName);
    setPatches(response.data || []);
  }, [packageName]);

  const fetchRunningPatchTasks = useCallback(async () => {
    try {
      const response = await getRunningPatchTasks(packageName);
      setRunningPatchTasks(response.data?.running_tasks || []);
    } catch {
      setRunningPatchTasks([]);
    }
  }, [packageName]);

  const loadWorkspace = useCallback(async () => {
    try {
      await Promise.all([fetchChannels(), fetchApks(), fetchPatches(), fetchRunningPatchTasks()]);
      setErrorMessage('');
    } catch (error) {
      setErrorMessage(
        `Could not load deployment data: ${error.response?.data?.detail || error.message}`
      );
    }
  }, [fetchApks, fetchChannels, fetchPatches, fetchRunningPatchTasks]);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchPatches();
      fetchRunningPatchTasks();
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchPatches, fetchRunningPatchTasks]);

  useEffect(() => {
    if (!apks.length) {
      setApkReferences({});
      return;
    }
    let active = true;
    Promise.all(
      apks.map(async apk => {
        try {
          const response = await checkApkReferences(packageName, apk.versioncode);
          return [apk.versioncode, response.data?.referenced_channels || []];
        } catch {
          return [apk.versioncode, []];
        }
      })
    ).then(entries => {
      if (active) {
        setApkReferences(Object.fromEntries(entries));
      }
    });
    return () => {
      active = false;
    };
  }, [apks, packageName]);

  const clearMessages = () => {
    setSuccessMessage('');
    setErrorMessage('');
  };

  const closeDrawer = () => {
    if (loading || uploading) {
      return;
    }
    setDrawer(null);
    setNewChannelName('');
    setNewChannelVersion('');
    setRenameOldName('');
    setRenameNewName('');
    setUploadFile(null);
    setUploadProgress(0);
    setPatchFromVersion('');
    setPatchToVersion('');
  };

  const openRename = channel => {
    setRenameOldName(channel.channel_name);
    setRenameNewName(channel.channel_name);
    setDrawer('rename-channel');
  };

  const handleCreateChannel = async () => {
    try {
      setLoading(true);
      clearMessages();
      await createChannel(packageName, {
        channel_name: newChannelName.trim(),
        latest_versioncode: Number(newChannelVersion),
      });
      setSuccessMessage(`Channel ${newChannelName.trim()} created.`);
      setDrawer(null);
      setNewChannelName('');
      setNewChannelVersion('');
      await fetchChannels();
    } catch (error) {
      setErrorMessage(`Could not create channel: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRenameChannel = async () => {
    try {
      setLoading(true);
      clearMessages();
      await renameChannel(packageName, renameOldName, renameNewName.trim());
      setSuccessMessage(`Channel renamed to ${renameNewName.trim()}.`);
      setDrawer(null);
      setRenameOldName('');
      setRenameNewName('');
      await fetchChannels();
    } catch (error) {
      setErrorMessage(`Could not rename channel: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateChannelVersion = async channel => {
    const versioncode = pendingVersions[channel.channel_name];
    if (versioncode === undefined || String(versioncode) === String(channel.latest_versioncode)) {
      return;
    }
    try {
      setLoading(true);
      clearMessages();
      const apkExistsResponse = await checkApkExists(packageName, versioncode);
      if (!apkExistsResponse.data?.exists) {
        setErrorMessage(`APK v${versioncode} is not in the artifact library.`);
        return;
      }
      await updateChannelVersion(packageName, channel.channel_name, Number(versioncode));
      setSuccessMessage(`${channel.channel_name} now targets v${versioncode}.`);
      setPendingVersions(current => {
        const next = { ...current };
        delete next[channel.channel_name];
        return next;
      });
      await Promise.all([fetchChannels(), fetchApks()]);
    } catch (error) {
      setErrorMessage(`Could not update target: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async () => {
    if (!uploadFile) {
      return;
    }
    try {
      setUploading(true);
      clearMessages();
      setUploadProgress(0);
      const response = await uploadApk(uploadFile, progressEvent => {
        setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
      });
      const uploaded = response.data || {};
      const versionLabel = uploaded.versioncode ? ` as ${formatApkVersion(uploaded)}` : '';
      if (uploaded.package_name && uploaded.package_name !== packageName) {
        // The APK belongs to another package: it was filed (and the app registered) there.
        setSuccessMessage(
          `${uploadFile.name} belongs to ${uploaded.package_name} and was added to that app's library${versionLabel}${
            uploaded.app_registered ? ' (app registered automatically)' : ''
          }.`
        );
      } else {
        setSuccessMessage(`${uploadFile.name} added to the APK library${versionLabel}.`);
      }
      setDrawer(null);
      setUploadFile(null);
      setUploadProgress(0);
      await fetchApks();
    } catch (error) {
      setErrorMessage(`Could not upload APK: ${error.response?.data?.detail || error.message}`);
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadApk = async versioncode => {
    try {
      setLoading(true);
      clearMessages();
      const response = await downloadApk(packageName, versioncode);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `terna_app_v${versioncode}.apk`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSuccessMessage(`Download for v${versioncode} started.`);
    } catch (error) {
      setErrorMessage(`Could not download APK: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePatch = async () => {
    if (!patchFromVersion || !patchToVersion || patchFromVersion === patchToVersion) {
      return;
    }
    try {
      setLoading(true);
      clearMessages();
      const response = await generatePatch(
        packageName,
        Number(patchFromVersion),
        Number(patchToVersion)
      );
      setSuccessMessage(response.data?.message || 'Patch generation started.');
      setDrawer(null);
      setPatchFromVersion('');
      setPatchToVersion('');
      await fetchRunningPatchTasks();
    } catch (error) {
      setErrorMessage(`Could not generate patch: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) {
      return;
    }
    try {
      setLoading(true);
      clearMessages();
      if (pendingDelete.type === 'channel') {
        await deleteChannel(packageName, pendingDelete.channelName);
        await fetchChannels();
      } else if (pendingDelete.type === 'apk') {
        await deleteApk(packageName, pendingDelete.versioncode);
        await fetchApks();
      } else {
        await deletePatch(packageName, pendingDelete.oldVersion, pendingDelete.newVersion);
        await fetchPatches();
      }
      setSuccessMessage(`${pendingDelete.label} deleted.`);
      setPendingDelete(null);
    } catch (error) {
      setErrorMessage(`Could not delete item: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = bytes => {
    if (!bytes) {
      return '0 B';
    }
    const units = ['B', 'KB', 'MB', 'GB'];
    const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    return `${(bytes / 1024 ** index).toFixed(index ? 1 : 0)} ${units[index]}`;
  };

  const formatDate = timestamp => new Date(timestamp * 1000).toLocaleString();

  const sortedApks = useMemo(
    () => [...apks].sort((a, b) => Number(b.versioncode) - Number(a.versioncode)),
    [apks]
  );

  const viewCount = { channels: channels.length, artifacts: apks.length, patches: patches.length };

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <span className={styles.eyebrow}>Release preparation · {packageName}</span>
          <h1>Channels &amp; artifacts</h1>
          <p>Prepare release files first, then choose which version each rollout channel serves.</p>
        </div>
      </header>

      {successMessage && (
        <Toast tone="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Toast>
      )}
      {errorMessage && (
        <Toast tone="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Toast>
      )}

      <nav className={styles.viewSwitcher} aria-label="Release preparation sections">
        {views.map(view => {
          const Icon = view.icon;
          return (
            <button
              key={view.id}
              type="button"
              className={activeView === view.id ? styles.viewActive : ''}
              onClick={() => setActiveView(view.id)}
            >
              <Icon size={17} />
              <span>{view.label}</span>
              <b>{viewCount[view.id]}</b>
            </button>
          );
        })}
      </nav>

      {activeView === 'channels' && (
        <section className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <h2>Rollout channels</h2>
              <p>
                Changing a target affects devices assigned to that channel on their next update
                check.
              </p>
            </div>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={() => {
                setNewChannelVersion(sortedApks[0]?.versioncode || '');
                setDrawer('create-channel');
              }}
            >
              <Plus size={16} /> New channel
            </button>
          </div>
          <div className={styles.channelGrid}>
            {channels.map(channel => {
              const selectedVersion =
                pendingVersions[channel.channel_name] ?? channel.latest_versioncode;
              const hasChange = String(selectedVersion) !== String(channel.latest_versioncode);
              return (
                <article className={styles.channelCard} key={channel.channel_name}>
                  <div className={styles.channelTitle}>
                    <span>
                      <Boxes size={17} />
                    </span>
                    <div>
                      <strong>{channel.channel_name}</strong>
                      <small>
                        {channel.channel_name === 'release'
                          ? 'Default production channel'
                          : 'Custom rollout channel'}
                      </small>
                    </div>
                    {channel.channel_name !== 'release' && (
                      <button
                        type="button"
                        className={styles.iconButton}
                        onClick={() => openRename(channel)}
                        aria-label={`Rename ${channel.channel_name}`}
                      >
                        <Pencil size={15} />
                      </button>
                    )}
                  </div>
                  <div className={styles.versionField}>
                    <label htmlFor={`channel-target-${encodeURIComponent(channel.channel_name)}`}>
                      Target APK version
                    </label>
                    <ApkSelect
                      inputId={`channel-target-${encodeURIComponent(channel.channel_name)}`}
                      apks={sortedApks}
                      value={selectedVersion}
                      onChange={versioncode =>
                        setPendingVersions(current => ({
                          ...current,
                          [channel.channel_name]: versioncode,
                        }))
                      }
                    />
                  </div>
                  <div className={styles.channelFooter}>
                    <>
                      {channel.channel_name !== 'release' && (
                        <button
                          type="button"
                          className={styles.dangerLink}
                          onClick={() =>
                            setPendingDelete({
                              type: 'channel',
                              channelName: channel.channel_name,
                              label: `Channel ${channel.channel_name}`,
                            })
                          }
                        >
                          <Trash2 size={14} /> Delete
                        </button>
                      )}
                      <button
                        type="button"
                        className={styles.saveButton}
                        disabled={!hasChange || loading}
                        onClick={() => handleUpdateChannelVersion(channel)}
                      >
                        {loading && hasChange ? 'Applying…' : 'Apply target'}
                      </button>
                    </>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      )}

      {activeView === 'artifacts' && (
        <section className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <h2>APK library</h2>
              <p>
                Uploaded files become selectable targets for rollout channels and patch generation.
              </p>
            </div>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={() => setDrawer('upload-apk')}
            >
              <UploadCloud size={16} /> Upload APK
            </button>
          </div>
          {sortedApks.length ? (
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Artifact</th>
                    <th>Version</th>
                    <th>Package</th>
                    <th>Used by</th>
                    <th>Size</th>
                    <th>Modified</th>
                    <th>
                      <span className={styles.srOnly}>Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedApks.map(apk => {
                    const references = apkReferences[apk.versioncode] || [];
                    return (
                      <tr key={apk.filename}>
                        <td data-label="Artifact">
                          <div className={styles.artifactName}>
                            <FileArchive size={17} />
                            <span>
                              <strong>{apk.filename}</strong>
                              <code title={apk.sha256}>{apk.sha256 || 'No checksum'}</code>
                            </span>
                          </div>
                        </td>
                        <td data-label="Version">
                          <div className={styles.versionCell}>
                            <strong>
                              {apk.version_name ? `v${apk.version_name}` : 'Unknown version'}
                            </strong>
                            <span className={styles.versionBadge} title="Version code">
                              {apk.versioncode}
                            </span>
                          </div>
                        </td>
                        <td data-label="Package">
                          {apk.package_name ? (
                            <code className={styles.packageName}>{apk.package_name}</code>
                          ) : (
                            <span className={styles.muted}>Unknown</span>
                          )}
                        </td>
                        <td data-label="Used by">
                          {references.length ? (
                            <div className={styles.tagList}>
                              {references.map(name => (
                                <span key={name}>{name}</span>
                              ))}
                            </div>
                          ) : (
                            <span className={styles.muted}>Not targeted</span>
                          )}
                        </td>
                        <td data-label="Size">{formatFileSize(apk.size)}</td>
                        <td data-label="Modified">{formatDate(apk.modified_time)}</td>
                        <td className={styles.rowActions}>
                          <>
                            <button
                              type="button"
                              className={styles.iconButton}
                              onClick={() => handleDownloadApk(apk.versioncode)}
                              title="Download APK"
                            >
                              <ArrowDownToLine size={15} />
                            </button>
                            <button
                              type="button"
                              className={styles.iconDangerButton}
                              onClick={() =>
                                setPendingDelete({
                                  type: 'apk',
                                  versioncode: apk.versioncode,
                                  label: `APK v${apk.versioncode}`,
                                })
                              }
                              disabled={references.length > 0}
                              title={
                                references.length ? 'Remove channel references first' : 'Delete APK'
                              }
                            >
                              <Trash2 size={15} />
                            </button>
                          </>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={styles.emptyState}>
              <PackagePlus size={27} />
              <strong>No APK artifacts yet</strong>
              <p>Upload an APK before configuring a channel target.</p>
            </div>
          )}
        </section>
      )}

      {activeView === 'patches' && (
        <section className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <h2>Delta patches</h2>
              <p>
                Generate smaller update packages between two APK versions already in the library.
              </p>
            </div>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={() => setDrawer('create-patch')}
              disabled={apks.length < 2}
            >
              <Plus size={16} /> Generate patch
            </button>
          </div>
          {runningPatchTasks.length > 0 && (
            <div className={styles.runningTasks}>
              {runningPatchTasks.map(task => (
                <div key={task.task_key}>
                  <span className={styles.pulse} />
                  <strong>
                    Generating v{task.old_version} → v{task.new_version}
                  </strong>
                  <small>This list refreshes automatically.</small>
                </div>
              ))}
            </div>
          )}
          {patches.length ? (
            <div className={styles.patchGrid}>
              {patches.map(patch => {
                return (
                  <article className={styles.patchCard} key={patch.filename}>
                    <div className={styles.patchRoute}>
                      <span>v{patch.old_version || '?'}</span>
                      <GitCompareArrows size={17} />
                      <span>v{patch.new_version || '?'}</span>
                    </div>
                    <strong>{patch.filename}</strong>
                    <div className={styles.patchMeta}>
                      <span>{formatFileSize(patch.size)}</span>
                      <span>{formatDate(patch.modified_time)}</span>
                    </div>
                    <button
                      type="button"
                      className={styles.dangerLink}
                      onClick={() =>
                        setPendingDelete({
                          type: 'patch',
                          oldVersion: patch.old_version,
                          newVersion: patch.new_version,
                          label: `Patch v${patch.old_version} to v${patch.new_version}`,
                        })
                      }
                    >
                      <Trash2 size={14} /> Delete patch
                    </button>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <GitCompareArrows size={27} />
              <strong>No delta patches</strong>
              <p>Devices will download full APKs until a matching patch is available.</p>
            </div>
          )}
        </section>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title={`Delete ${pendingDelete?.label}?`}
        description="This cannot be undone."
        confirmLabel="Delete"
        busyLabel="Deleting…"
        busy={loading}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      <ActionDrawer
        open={drawer === 'create-channel'}
        eyebrow="Rollout channels"
        title="Create a channel"
        description="Use channels to stage releases for a defined group of devices."
        onClose={closeDrawer}
        closeDisabled={loading}
        footer={
          <>
            <button type="button" className={styles.secondaryButton} onClick={closeDrawer}>
              Cancel
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleCreateChannel}
              disabled={loading || !newChannelName.trim() || !newChannelVersion}
            >
              {loading ? 'Creating…' : 'Create channel'}
            </button>
          </>
        }
      >
        <div className={styles.drawerForm}>
          <label>
            <span>Channel name</span>
            <input
              value={newChannelName}
              onChange={event => setNewChannelName(event.target.value)}
              placeholder="e.g. pilot"
              autoFocus
            />
          </label>
          <div className={styles.field}>
            <label htmlFor="new-channel-target">Initial target</label>
            <ApkSelect
              inputId="new-channel-target"
              apks={sortedApks}
              value={newChannelVersion}
              onChange={setNewChannelVersion}
              placeholder="Choose the APK this channel serves"
            />
          </div>
        </div>
      </ActionDrawer>

      <ActionDrawer
        open={drawer === 'rename-channel'}
        eyebrow="Rollout channels"
        title="Rename channel"
        description={`Current name: ${renameOldName}`}
        onClose={closeDrawer}
        closeDisabled={loading}
        footer={
          <>
            <button type="button" className={styles.secondaryButton} onClick={closeDrawer}>
              Cancel
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleRenameChannel}
              disabled={loading || !renameNewName.trim() || renameNewName.trim() === renameOldName}
            >
              {loading ? 'Renaming…' : 'Rename channel'}
            </button>
          </>
        }
      >
        <div className={styles.drawerForm}>
          <label>
            <span>New channel name</span>
            <input
              value={renameNewName}
              onChange={event => setRenameNewName(event.target.value)}
              autoFocus
            />
          </label>
        </div>
      </ActionDrawer>

      <ActionDrawer
        open={drawer === 'upload-apk'}
        eyebrow="APK library"
        title="Upload an APK"
        description={`The backend reads the version code, version name, package name and checksum from the file. An APK whose package is not ${packageName} is filed under its own app instead.`}
        onClose={closeDrawer}
        closeDisabled={uploading}
        footer={
          <>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={closeDrawer}
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleFileUpload}
              disabled={uploading || !uploadFile}
            >
              {uploading ? `Uploading ${uploadProgress}%` : 'Upload APK'}
            </button>
          </>
        }
      >
        <div className={styles.drawerForm}>
          <label className={styles.fileDrop}>
            <UploadCloud size={28} />
            <span>{uploadFile ? uploadFile.name : 'Choose an APK file'}</span>
            <small>
              {uploadFile ? formatFileSize(uploadFile.size) : 'Only .apk files are accepted'}
            </small>
            <input
              type="file"
              accept=".apk"
              onChange={event => setUploadFile(event.target.files?.[0] || null)}
            />
          </label>
          {uploading && (
            <progress className={styles.progress} max="100" value={uploadProgress}>
              {uploadProgress}%
            </progress>
          )}
        </div>
      </ActionDrawer>

      <ActionDrawer
        open={drawer === 'create-patch'}
        eyebrow="Delta patches"
        title="Generate a patch"
        description="Choose the installed source version and the target version."
        onClose={closeDrawer}
        closeDisabled={loading}
        footer={
          <>
            <button type="button" className={styles.secondaryButton} onClick={closeDrawer}>
              Cancel
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleGeneratePatch}
              disabled={
                loading ||
                !patchFromVersion ||
                !patchToVersion ||
                patchFromVersion === patchToVersion
              }
            >
              {loading ? 'Starting…' : 'Start generation'}
            </button>
          </>
        }
      >
        <div className={styles.drawerForm}>
          <div className={styles.field}>
            <label htmlFor="patch-from-version">From version</label>
            <ApkSelect
              inputId="patch-from-version"
              apks={sortedApks}
              value={patchFromVersion}
              onChange={setPatchFromVersion}
              placeholder="Choose source APK"
            />
          </div>
          <div className={styles.routeArrow}>↓</div>
          <div className={styles.field}>
            <label htmlFor="patch-to-version">To version</label>
            <ApkSelect
              inputId="patch-to-version"
              apks={sortedApks}
              value={patchToVersion}
              onChange={setPatchToVersion}
              placeholder="Choose target APK"
            />
          </div>
        </div>
      </ActionDrawer>
    </div>
  );
}

export default AppUpdateChannelMgr;
